import React from 'react';
import appConfig from '../config/appConfig';

const styles = {
  wrapper: {
    backgroundColor: '#f9fafb',
    minHeight: '100vh',
    padding: '40px 20px',
  },
  container: {
    maxWidth: '780px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
    padding: '48px 56px',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    color: '#1a1a1a',
    lineHeight: 1.7,
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    marginBottom: '4px',
    color: '#111827',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '36px',
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    marginTop: '32px',
    marginBottom: '12px',
  },
  paragraph: {
    fontSize: '15px',
    color: '#374151',
    marginBottom: '16px',
  },
  list: {
    fontSize: '15px',
    color: '#374151',
    paddingLeft: '24px',
    marginBottom: '16px',
  },
  listItem: {
    marginBottom: '8px',
  },
  link: {
    color: '#2563eb',
    textDecoration: 'none',
    fontWeight: 500,
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #e5e7eb',
    margin: '32px 0',
  },
};

export default function PrivacyPage() {
  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <h1 style={styles.title}>Privacy Policy</h1>
        <p style={styles.subtitle}>
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>

        <h3 style={styles.sectionTitle}>Privacy-first by design</h3>
        <p style={styles.paragraph}>
          Timeline respects your privacy. This Privacy Policy explains what data
          we collect, how we use it, and your rights when you use the Timeline mobile application.
        </p>
        <p style={styles.paragraph}>
          You must be at least 18 years old to use Timeline. We do not knowingly collect data from
          users under 18. If we learn that such data has been collected, we will delete it.
        </p>

        <h3 style={styles.sectionTitle}>Data we collect</h3>
        <p style={styles.paragraph}>
          We may collect the following categories of data when you use the Timeline app:
        </p>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <strong>Account and profile data:</strong> Google account identifier, display name,
            email address, profile photo, username, bio, photos, age range, gender, and optional
            social links.
          </li>
          <li style={styles.listItem}>
            <strong>Plans and invites:</strong> Plan details and invite activity to run plans,
            timelines, and safety features.
          </li>
          <li style={styles.listItem}>
            <strong>Device and usage data:</strong> Device type, OS version, app version, crash
            logs, feature usage, and push notification tokens.
          </li>
        </ul>

        <h3 style={styles.sectionTitle}>How we use your data</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>Create and manage your account and profile.</li>
          <li style={styles.listItem}>Provide plans, invites, memories, and timeline features.</li>
          <li style={styles.listItem}>Send notifications related to app activity.</li>
          <li style={styles.listItem}>Process premium subscriptions through Google Play.</li>
          <li style={styles.listItem}>Improve app performance, stability, and user experience.</li>
          <li style={styles.listItem}>Comply with legal obligations and enforce our Terms.</li>
        </ul>

        <h3 style={styles.sectionTitle}>Analytics</h3>
        <p style={styles.paragraph}>
          We may use privacy-focused analytics and crash reporting tools (such as Firebase Analytics
          and Crashlytics) to understand app usage and fix issues.
        </p>

        <h3 style={styles.sectionTitle}>Sharing with third parties</h3>
        <p style={styles.paragraph}>
          We do not sell your personal data. We may share data with trusted service providers such
          as Google (Sign-In, Play Store, Firebase) solely to operate the service or when
          required by law.
        </p>

        <h3 style={styles.sectionTitle}>Data retention</h3>
        <p style={styles.paragraph}>
          We retain your data while your account is active. When you delete your account, your data
          is deleted or anonymised within a reasonable period.
        </p>

        <h3 style={styles.sectionTitle}>Your rights</h3>
        <p style={styles.paragraph}>
          You may access, correct, or delete your personal data by contacting us. You may also
          object to or restrict certain processing where permitted by law.
        </p>

        <h3 style={styles.sectionTitle}>Security</h3>
        <p style={styles.paragraph}>
          We use industry-standard security measures to protect your data, including encrypted
          communications. However, no system can be guaranteed to be completely secure.
        </p>

        <h3 style={styles.sectionTitle}>International processing</h3>
        <p style={styles.paragraph}>
          Your data may be processed in India and other countries where our service providers
          operate, with appropriate safeguards in place.
        </p>

        <h3 style={styles.sectionTitle}>Policy updates</h3>
        <p style={styles.paragraph}>
          We may update this Privacy Policy from time to time. Continued use of the app after
          updates means you accept the revised policy.
        </p>

        <hr style={styles.divider} />

        <h3 style={styles.sectionTitle}>Support</h3>
        <p style={styles.paragraph}>
          For privacy questions or requests, email{' '}
          <a href={`mailto:${appConfig.supportEmail}`} style={styles.link}>
            {appConfig.supportEmail}
          </a>.
        </p>
      </div>
    </div>
  );
}